import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import SystemSelector from './SystemSelector';
import { getHealthSystems, syncAuthTokens } from '../../../utils/api';
import { mapSystemsResponse, slugifySystemName } from '../utils/systemUtils';
import { AuthContext } from '../../../context/AuthContext';
import '../styles.css';

const SystemSelectionPage = () => {
  const [systemsList, setSystemsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  // Get tokens from main app's AuthContext
  const authContext = useContext(AuthContext);
  const accessToken = authContext?.accessToken;
  const refreshToken = authContext?.refreshToken;
  
  // Sync tokens with the api utility when they're available
  useEffect(() => {
    if (accessToken && refreshToken) {
      syncAuthTokens(accessToken, refreshToken);
    }
  }, [accessToken, refreshToken]);

  // Fetch health systems from the API
  useEffect(() => {
    let isMounted = true;

    const loadSystems = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getHealthSystems();
        const formattedSystems = mapSystemsResponse(response);

        if (!isMounted) {
          return;
        }

        if (!Array.isArray(response?.systems)) {
          console.warn('Unexpected health systems response format:', response);
        }

        setSystemsList(formattedSystems);
        if (formattedSystems.length === 0) {
          setError('No health systems available at this time.');
        }
      } catch (err) {
        if (!isMounted) {
          return;
        }
        setError(err.message || 'Failed to load health systems. Please try again later.');
        console.error('Error loading health systems:', err);
        setSystemsList([]); // Ensure list is empty on error
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadSystems();

    return () => {
      isMounted = false;
    };
  }, []);

  // Handle system selection
  const handleSystemSelect = (system) => {
    // Navigate to the system detail page using the system name as the URL parameter
    // Convert system name to URL-friendly format
    // The 'id' in the system object passed here IS the system name from the API
    const systemNameForUrl = slugifySystemName(system.id);
    // Pass the selected system object (which includes name and patientCount) in state
    navigate(`/asthma_tracker/${systemNameForUrl}`, { state: { system } });
  };

  if (loading) {
    return <div className="loading-container">Loading Asthma Tracker data...</div>;
  }

  if (error) {
    return <div className="error-container">{error}</div>;
  }

  return (
    <div className="asthma-tracker-container">
      <h1 className="page-title">Asthma Tracker</h1>
      
      {/* System Selector Section */}
      <div className="system-selector-section">
        <h2>Select Health System</h2>
        <p className="selection-instruction">
          Choose a health system to view detailed asthma metrics and analytics.
        </p>
        <SystemSelector 
          systems={systemsList} 
          selectedSystem={null}
          onSelectSystem={handleSystemSelect}
        />
      </div>
    </div>
  );
};

export default SystemSelectionPage; 